import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "./ui/Select";
import toast from "react-hot-toast";
import { PlusCircle, Save, Trash2 } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import { iranCustoms } from "../constants/iranCustoms";
import { countries } from "../constants/countryList";
import InvoicePartyQuickCreate from "./InvoicePartyQuickCreate";
import { getApiErrorLines } from "../utils/formErrors";
import { ERROR_TOAST_OPTIONS, formatErrorLines, toastErrors } from "../utils/toastErrors";
import { PROFORMA_API_ERROR_LABELS, validateProformaInvoiceForm } from "../utils/invoiceValidation";
import { toLatinDigits } from "../utils/numberFormat";

const currencyOptions = [
  { value: "USD", label: "دلار آمریکا" },
  { value: "EUR", label: "یورو" },
  { value: "CNY", label: "یوان چین" },
  { value: "GBP", label: "پوند استرلینگ" },
  { value: "IRR", label: "ریال" },
  { value: "AED", label: "درهم امارات" },
];

const meansOfTransportOptions = [
  { value: "By Truck", label: "کامیون" },
  { value: "By AirPlane", label: "هواپیما" },
  { value: "By Vessel", label: "کشتی" },
  { value: "By Train", label: "قطار" },
];

const termsOfDeliveryOptions = [
  { value: "CFR", label: "CFR" },
  { value: "CIF", label: "CIF" },
  { value: "CIP", label: "CIP" },
  { value: "CPT", label: "CPT" },
  { value: "DAP", label: "DAP" },
  { value: "DDP", label: "DDP" },
  { value: "DPU", label: "DPU" },
  { value: "EXW", label: "EXW" },
  { value: "FAS", label: "FAS" },
  { value: "FCA", label: "FCA" },
  { value: "FOB", label: "FOB" },
];

const termsOfPaymentOptions = [
  { value: "T/T", label: "T/T" },
  { value: "L/C", label: "L/C" },
  { value: "D/P", label: "D/P" },
  { value: "D/A", label: "D/A" },
];

const standardOptions = [
  { value: "JIS", label: "JIS" },
  { value: "ISO", label: "ISO" },
];

const unitOptions = [
  { value: "KG", label: "KG" },
  { value: "M3", label: "M3" },
  { value: "M2", label: "M2" },
  { value: "M", label: "M" },
  { value: "PCS", label: "PCS" },
  { value: "SET", label: "SET" },
];

const createItem = () => ({
  description: "",
  quantity: 1,
  unit_price: 0,
  nw_kg: 0,
  gw_kg: 0,
  unit: "PCS",
  commodity_code: "",
  origin: "",
});

const selectStyles = {
  control: (base) => ({
    ...base,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderColor: "#E5E7EB",
    borderRadius: "0.75rem",
    paddingBlock: "2px",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    minHeight: "42px",
    ":hover": { borderColor: "#2563EB" },
  }),
  placeholder: (base) => ({ ...base, color: "#9CA3AF", textAlign: "right" }),
  singleValue: (base) => ({ ...base, color: "#111827", textAlign: "right" }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#EEF2FF",
    borderRadius: "0.5rem",
  }),
  multiValueLabel: (base) => ({ ...base, color: "#111827" }),
  multiValueRemove: (base) => ({
    ...base,
    ":hover": { backgroundColor: "#2563EB", color: "#fff" },
  }),
  menu: (base) => ({
    ...base,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderRadius: "0.75rem",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    textAlign: "right",
    backgroundColor: state.isSelected ? "#2563EB" : state.isFocused ? "#DBEAFE" : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
  }),
};

export default function ProformaInvoiceForm() {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [quickCreateType, setQuickCreateType] = useState(null);
  const [invoiceData, setInvoiceData] = useState({
    seller: "",
    buyer: "",
    proforma_invoice_number: "",
    proforma_invoice_currency: "AED",
    proforma_freight_charges: 0,
    terms_of_delivery: "CPT",
    terms_of_payment: "T/T",
    standard: "JIS",
    partial_shipment: false,
    relevant_location: "",
    means_of_transport: "By Vessel",
    country_of_origin: "",
    port_of_loading: "",
    proforma_invoice_date: "",
    proforma_invoice_exp_date: "",
    items: [createItem()],
  });

  useEffect(() => {
    axiosInstance
      .get("documents/sellers/")
      .then((res) => setSellers(res.data ?? []))
      .catch(() => toast.error("دریافت فهرست فروشندگان ناموفق بود."));
    axiosInstance
      .get("documents/buyers/")
      .then((res) => setBuyers(res.data ?? []))
      .catch(() => toast.error("دریافت فهرست خریداران ناموفق بود."));
  }, []);

  const sellerOptions = sellers.map((seller) => ({
    value: seller.id,
    label: seller.seller_name,
  }));

  const buyerOptions = buyers.map((buyer) => ({
    value: buyer.id,
    label: buyer.buyer_name,
  }));

  const iranCustomsOptions = iranCustoms.map((custom) => ({
    value: custom.ctmNameStr,
    label: `${custom.ctmNameStr} (${custom.ctmVCodeInt})`,
  }));

  const countryOptions = countries.map((country) => ({
    value: country.name,
    label: `${country.name} (${country.persianName})`,
  }));

  const { subTotal, totalQty, totalNW, totalGW } = useMemo(() => {
    const subTotal = invoiceData.items.reduce(
      (sum, item) => sum + (parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0),
      0
    );
    const totalQty = invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    const totalNW = invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.nw_kg) || 0), 0);
    const totalGW = invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.gw_kg) || 0), 0);
    return { subTotal, totalQty, totalNW, totalGW };
  }, [invoiceData.items]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setInvoiceData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleItemChange = (index, event) => {
    const { name, value } = event.target;
    setInvoiceData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], [name]: value };
      return { ...prev, items: nextItems };
    });
  };

  const setSelectValue = (field, selectedOption, isMulti = false) => {
    setInvoiceData((prev) => ({
      ...prev,
      [field]: isMulti
        ? (selectedOption ? selectedOption.map((option) => option.value).join("-") : "")
        : (selectedOption ? selectedOption.value : ""),
    }));
  };

  const setItemSelectValue = (index, field, selectedOption, isMulti = false) => {
    setInvoiceData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = {
        ...nextItems[index],
        [field]: isMulti
          ? (selectedOption ? selectedOption.map((option) => option.value).join("-") : "")
          : (selectedOption ? selectedOption.value : ""),
      };
      return { ...prev, items: nextItems };
    });
  };

  const addItemRow = () => {
    setInvoiceData((prev) => ({ ...prev, items: [...prev.items, createItem()] }));
  };

  const removeItemRow = (index) => {
    setInvoiceData((prev) => ({
      ...prev,
      items: prev.items.length === 1 ? prev.items : prev.items.filter((_, i) => i !== index),
    }));
  };

  const handlePartyCreated = (party) => {
    if (quickCreateType === "seller") {
      setSellers((previous) => [...previous, party]);
      setInvoiceData((previous) => ({ ...previous, seller: party.id }));
      return;
    }

    setBuyers((previous) => [...previous, party]);
    setInvoiceData((previous) => ({ ...previous, buyer: party.id }));
  };

  const validateForm = () => {
    const problems = validateProformaInvoiceForm(invoiceData);
    if (problems.length) toastErrors(problems);
    return problems.length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const payload = {
      seller: invoiceData.seller,
      buyer: invoiceData.buyer,
      proforma_invoice_number: invoiceData.proforma_invoice_number,
      proforma_invoice_currency: invoiceData.proforma_invoice_currency,
      proforma_freight_charges: parseFloat(invoiceData.proforma_freight_charges) || 0,
      terms_of_delivery: invoiceData.terms_of_delivery,
      terms_of_payment: invoiceData.terms_of_payment,
      standard: invoiceData.standard,
      partial_shipment: invoiceData.partial_shipment,
      relevant_location: invoiceData.relevant_location,
      means_of_transport: invoiceData.means_of_transport,
      country_of_origin: invoiceData.country_of_origin,
      port_of_loading: invoiceData.port_of_loading,
      proforma_invoice_date: invoiceData.proforma_invoice_date,
      proforma_invoice_exp_date: invoiceData.proforma_invoice_exp_date,
      items: invoiceData.items.map((item) => ({
        original_description: item.description,
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0,
        nw_kg: parseFloat(item.nw_kg) || 0,
        gw_kg: parseFloat(item.gw_kg) || 0,
        commodity_code: String(item.commodity_code),
        unit: item.unit,
        origin: item.origin,
      })),
    };

    try {
      await toast.promise(
        axiosInstance.post("documents/proforma-invoices/", payload),
        {
          loading: "در حال ایجاد پروفورما...",
          success: "پروفورما با موفقیت ایجاد شد.",
          error: (error) =>
            formatErrorLines(
              getApiErrorLines(error, PROFORMA_API_ERROR_LABELS, "ایجاد پروفورما با خطا مواجه شد."),
            ),
        },
        { error: ERROR_TOAST_OPTIONS },
      );
      navigate("/proforma-invoices/list");
    } catch {
      // toast.promise shows the error.
    }
  };

  const getMultiValue = (value, options) =>
    value ? value.split("-").map((part) => options.find((option) => option.value === part.trim())).filter(Boolean) : [];

  const renderSelectField = (label, options, field, placeholder, isMulti = false) => (
    <div className="space-y-1">
      <label className="text-sm text-gray-600">{label}</label>
      <Select
        isMulti={isMulti}
        options={options}
        value={
          isMulti
            ? getMultiValue(invoiceData[field], options)
            : options.find((option) => option.value === invoiceData[field]) || null
        }
        onChange={(selected) => setSelectValue(field, selected, isMulti)}
        placeholder={placeholder}
        styles={selectStyles}
        isClearable
      />
    </div>
  );

  const renderPartySelectField = (label, options, field, placeholder, type) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm text-gray-600">{label}</label>
        <button
          type="button"
          onClick={() => setQuickCreateType(type)}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
        >
          <PlusCircle className="h-4 w-4" />
          {type === "seller" ? "فروشنده جدید" : "خریدار جدید"}
        </button>
      </div>
      <Select
        options={options}
        value={options.find((option) => option.value === invoiceData[field]) || null}
        onChange={(selected) => setSelectValue(field, selected)}
        placeholder={placeholder}
        styles={selectStyles}
        isClearable
      />
    </div>
  );

  return (
    <>
    <form dir="rtl" onSubmit={handleSubmit} noValidate className="font-vazir mt-20 md:mt-24 px-3">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold">ایجاد پروفورما</h2>
          <p className="text-sm text-gray-500 mt-1">اطلاعاتی را وارد کنید که بک‌اند برای ثبت پروفورما نیاز دارد.</p>
        </div>
      </div>

      <div className="rounded-2xl border bg-white shadow-sm">
        <div className="border-b px-4 md:px-6 py-4">
          <h3 className="text-base font-semibold">اطلاعات پایه</h3>
        </div>
        <div className="px-4 md:px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderPartySelectField("فروشنده", sellerOptions, "seller", "انتخاب فروشنده", "seller")}
            {renderPartySelectField("خریدار", buyerOptions, "buyer", "انتخاب خریدار", "buyer")}

            <div className="space-y-1">
              <label className="text-sm text-gray-600">شماره پروفورما</label>
              <input
                name="proforma_invoice_number"
                value={invoiceData.proforma_invoice_number}
                onChange={handleChange}
                placeholder="شماره پروفورما"
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-gray-600">تاریخ پروفورما</label>
              <input
                type="date"
                name="proforma_invoice_date"
                value={invoiceData.proforma_invoice_date}
                onChange={handleChange}
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            {renderSelectField("ارز", currencyOptions, "proforma_invoice_currency", "انتخاب ارز")}

            <div className="space-y-1">
              <label className="text-sm text-gray-600">هزینه حمل</label>
              <input
                type="number"
                name="proforma_freight_charges"
                value={invoiceData.proforma_freight_charges}
                onChange={handleChange}
                onWheel={(event) => event.currentTarget.blur()}
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm text-gray-600">تاریخ اعتبار</label>
              <input
                type="date"
                name="proforma_invoice_exp_date"
                value={invoiceData.proforma_invoice_exp_date}
                onChange={handleChange}
                className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            {renderSelectField("کشور مبدا", countryOptions, "country_of_origin", "انتخاب کشور مبدا", true)}
            {renderSelectField("بندر بارگیری", countryOptions, "port_of_loading", "انتخاب بندر بارگیری", true)}
            {renderSelectField("شرایط تحویل", termsOfDeliveryOptions, "terms_of_delivery", "انتخاب شرایط تحویل")}
            {renderSelectField("شرایط پرداخت", termsOfPaymentOptions, "terms_of_payment", "انتخاب شرایط پرداخت")}
            {renderSelectField("استاندارد", standardOptions, "standard", "انتخاب استاندارد")}
            {renderSelectField("وسیله حمل", meansOfTransportOptions, "means_of_transport", "انتخاب وسیله حمل", true)}
            {renderSelectField("گمرک مقصد", iranCustomsOptions, "relevant_location", "انتخاب گمرک مقصد", true)}

            <div className="space-y-1 flex items-center justify-end gap-2">
              <label className="text-sm text-gray-600">حمل به دفعات</label>
              <input
                type="checkbox"
                name="partial_shipment"
                checked={invoiceData.partial_shipment}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-600 border-gray-300 rounded ml-2"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden mt-6">
        <div className="border-b px-4 md:px-6 py-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">کالاها</h3>
          <button
            type="button"
            onClick={addItemRow}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            <PlusCircle className="w-4 h-4" />
            افزودن کالا
          </button>
        </div>

        <div className="px-4 md:px-6 py-6 space-y-4">
          {invoiceData.items.map((item, index) => (
            <div key={index} className="rounded-xl border p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-sm text-gray-600">شرح کالا</label>
                  <textarea
                    name="description"
                    value={item.description}
                    onChange={(event) => handleItemChange(index, event)}
                    className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y"
                  />
                </div>

                {[
                  ["quantity", "تعداد"],
                  ["unit_price", "قیمت واحد"],
                  ["nw_kg", "وزن خالص (کیلوگرم)"],
                  ["gw_kg", "وزن ناخالص (کیلوگرم)"],
                ].map(([field, label]) => (
                  <div key={field} className="space-y-1">
                    <label className="text-sm text-gray-600">{label}</label>
                    <input
                      type="number"
                      step={field === "unit_price" || field.includes("_kg") ? "0.01" : "1"}
                      name={field}
                      value={item[field]}
                      onChange={(event) => handleItemChange(index, event)}
                      onWheel={(event) => event.currentTarget.blur()}
                      className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                  </div>
                ))}

                <div className="space-y-1">
                  <label className="text-sm text-gray-600">کد کالا (HS)</label>
                  {/* Text, not number: HS codes can start with 0. */}
                  <input
                    type="text"
                    inputMode="numeric"
                    dir="ltr"
                    maxLength={9}
                    name="commodity_code"
                    value={item.commodity_code}
                    onChange={(event) =>
                      handleItemChange(index, {
                        target: {
                          name: "commodity_code",
                          value: toLatinDigits(event.target.value).replace(/\D/g, "").slice(0, 9),
                        },
                      })
                    }
                    placeholder="حداکثر ۹ رقم"
                    className="w-full rounded-xl border px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm text-gray-600">واحد</label>
                  <Select
                    options={unitOptions}
                    value={unitOptions.find((option) => option.value === item.unit) || null}
                    onChange={(selected) => setItemSelectValue(index, "unit", selected)}
                    placeholder="انتخاب واحد"
                    styles={selectStyles}
                    isClearable
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm text-gray-600">مبدا</label>
                  <Select
                    isMulti
                    options={countryOptions}
                    value={getMultiValue(item.origin, countryOptions)}
                    onChange={(selected) => setItemSelectValue(index, "origin", selected, true)}
                    placeholder="انتخاب مبدا"
                    styles={selectStyles}
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  جمع خطی:{" "}
                  <span className="font-semibold text-gray-900">
                    {((parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeItemRow(index)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-red-600 text-white hover:bg-red-700 transition"
                >
                  <Trash2 className="w-4 h-4" />
                  حذف کالا
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-3 mt-6 z-10">
        <div className="mx-auto w-full md:w-auto md:ml-auto flex flex-col md:flex-row gap-2 md:justify-end bg-white/80 backdrop-blur rounded-2xl border p-3 shadow-sm">
          <div className="flex-1 md:flex-none md:px-3 md:py-2 rounded-xl border bg-gray-50 text-sm text-gray-700">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>جمع فرعی: <b>{subTotal.toFixed(2)}</b></span>
              <span>تعداد کل: <b>{totalQty}</b></span>
              <span>NW: <b>{totalNW}</b></span>
              <span>GW: <b>{totalGW}</b></span>
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            <Save className="w-4 h-4" />
            ذخیره پروفورما
          </button>
        </div>
      </div>
    </form>
      {quickCreateType ? (
        <InvoicePartyQuickCreate
          type={quickCreateType}
          onClose={() => setQuickCreateType(null)}
          onCreated={handlePartyCreated}
        />
      ) : null}
    </>
  );
}
